import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import useDataStore from "../../store/dataStore";

const BASE_URL = "https://mis.empowerplus.in";

export default function PrePostScorePage() {
    const token = localStorage.getItem("token");
    const store = useDataStore();
    const CACHE_KEY = "prePostScores";

    /* ================= COMMON ================= */
    const [msg, setMsg] = useState({ type: "", text: "" });
    const flash = (type, text) => setMsg({ type, text });

    const authHeaders = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token]
    );

    /* ================= EXCEL UPLOAD ================= */
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);

    const CHUNK_SIZE = 500;
    const PARALLEL = 2;

    const uploadExcel = async () => {
        if (!file) return flash("err", "Select a Pre/Post Score Excel file");
        let dataRows;
        let headerRow;
        try {
            const buffer = await file.arrayBuffer();
            const wb = XLSX.read(buffer, { type: "array" });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
            headerRow = raw[0];
            dataRows = raw.slice(1);
        } catch (err) {
            return flash("err", `Cannot read file: ${err.message}`);
        }
        if (!dataRows.length) return flash("err", "Excel file is empty");
        const totalRows = dataRows.length;
        const totalChunks = Math.ceil(totalRows / CHUNK_SIZE);
        setUploading(true);
        setUploadProgress({ uploaded: 0, total: totalRows, chunk: 0, totalChunks });
        let uploadedSoFar = 0;
        let totalInserted = 0;
        let totalSkipped = 0;
        let failed = false;
        let firstError = "";

        const uploadOneChunk = async (i) => {
            const chunkRows = [headerRow, ...dataRows.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE)];
            const res = await fetch(`${BASE_URL}/api/pre-post-scores/upload-json`, {
                method: "POST",
                headers: { ...authHeaders, "Content-Type": "application/json" },
                body: JSON.stringify(chunkRows),
            });
            if (res.status === 413) throw new Error(`Chunk ${i + 1}: Payload too large (413)`);
            const text = await res.text();
            let data;
            try { data = JSON.parse(text); } catch { data = text; }
            if (!res.ok) throw new Error(`Chunk ${i + 1}: ${data?.message || `HTTP ${res.status}`}`);
            return { inserted: data?.inserted ?? 0, skipped: data?.skipped ?? 0 };
        };

        for (let batchStart = 0; batchStart < totalChunks && !failed; batchStart += PARALLEL) {
            const batchEnd = Math.min(batchStart + PARALLEL, totalChunks);
            const batchIndices = Array.from({ length: batchEnd - batchStart }, (_, k) => batchStart + k);
            setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: batchEnd, totalChunks });
            const results = await Promise.allSettled(batchIndices.map(uploadOneChunk));
            for (const result of results) {
                if (result.status === "fulfilled") {
                    totalInserted += result.value.inserted;
                    totalSkipped += result.value.skipped;
                    uploadedSoFar += result.value.inserted + result.value.skipped;
                } else {
                    failed = true;
                    firstError = result.reason?.message || "Upload failed";
                }
            }
            setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: batchEnd, totalChunks, error: failed });
        }

        setUploading(false);
        if (!failed) {
            flash("ok", `Upload complete — Inserted: ${totalInserted.toLocaleString()}, Skipped: ${totalSkipped.toLocaleString()} (${totalChunks} chunks)`);
            setFile(null);
            setUploadProgress(null);
            store.invalidate(CACHE_KEY);
            fetchAllScores();
        } else {
            flash("err", firstError);
            setUploadProgress(prev => prev ? { ...prev, error: true } : null);
        }
    };

    /* ================= TEMPLATE ================= */
    const downloadTemplate = () => {
        const ws = XLSX.utils.aoa_to_sheet([
            ["Beneficiary ID", "Beneficiary Name", "Pre Test Score", "Post Test Score"],
            ["BEN001", "Rahul Kumar", 72, 88],
            ["BEN002", "Priya Sharma", 65, 81],
        ]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Template");
        XLSX.writeFile(wb, "pre_post_score_template.xlsx");
    };

    /* ================= TABLE ================= */
    const [scores, setScores] = useState(() => store.getRows(CACHE_KEY));
    const [loading, setLoading] = useState(false);
    const [loadingAll, setLoadingAll] = useState(false);
    const [allLoaded, setAllLoaded] = useState(() => store.isAllLoaded(CACHE_KEY));
    const [search, setSearch] = useState("");
    const [visibleCount, setVisibleCount] = useState(100);

    const fetchFirst100 = async () => {
        const cached = store.getRows(CACHE_KEY);
        if (store.isCached(CACHE_KEY) && (cached.length > 0 || store.isAllLoaded(CACHE_KEY))) {
            setScores(cached);
            setAllLoaded(store.isAllLoaded(CACHE_KEY));
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`${BASE_URL}/api/pre-post-scores?page=0&size=100`, { headers: authHeaders });
            const data = await res.json();
            const all = Array.isArray(data) ? data : [];
            setScores(all);
            setAllLoaded(false);
            store.setRows(CACHE_KEY, all, false);
        } catch {
            setScores([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllScores = async () => {
        setLoadingAll(true);
        try {
            const res = await fetch(`${BASE_URL}/api/pre-post-scores`, { headers: authHeaders });
            const data = await res.json();
            const all = Array.isArray(data) ? data : [];
            setScores(all);
            setAllLoaded(true);
            setVisibleCount(all.length);
            store.markAllLoaded(CACHE_KEY, all);
        } catch {
        } finally {
            setLoadingAll(false);
        }
    };

    useEffect(() => { fetchFirst100(); }, []);
    useEffect(() => { setVisibleCount(100); }, [search]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return scores.filter(
            (s) =>
                s.beneficiaryId?.toString().toLowerCase().includes(q) ||
                s.beneficiaryName?.toLowerCase().includes(q) ||
                s.batchId?.toString().toLowerCase().includes(q) ||
                s.centerId?.toString().toLowerCase().includes(q)
        );
    }, [scores, search]);

    const [exporting, setExporting] = useState(false);

    const exportExcel = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            let data = scores;
            if (!allLoaded) {
                const res = await fetch(`${BASE_URL}/api/pre-post-scores`, { headers: authHeaders });
                const json = await res.json();
                data = Array.isArray(json) ? json : [];
                setScores(data);
                setAllLoaded(true);
                setVisibleCount(data.length);
                store.markAllLoaded(CACHE_KEY, data);
            }
            if (!data.length) return flash("err", "No data to export");
            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "PrePostScores");
            XLSX.writeFile(wb, "pre_post_scores.xlsx");
            flash("ok", `Exported ${data.length.toLocaleString()} records`);
        } catch (e) {
            flash("err", "Export failed: " + e.message);
        } finally {
            setExporting(false);
        }
    };

    /* ================= DELETE ================= */
    const deleteScore = async (id) => {
        if (!window.confirm("Delete this pre/post score record?")) return;
        try {
            const res = await fetch(`${BASE_URL}/api/pre-post-scores/${id}`, {
                method: "DELETE",
                headers: authHeaders,
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            flash("ok", "Record deleted");
            store.invalidate(CACHE_KEY);
            fetchAllScores();
        } catch (err) {
            flash("err", `Delete failed: ${err.message}`);
        }
    };

    /* ================= UI ================= */
    const COL_COUNT = 10;

    return (
        <div className="cmp-page">
            <h1>Pre / Post Score Management</h1>

            {msg.text && (
                <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span>{msg.text}</span>
                    <button onClick={() => setMsg({ type: "", text: "" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
                </div>
            )}

            {uploadProgress && (
                <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "14px 18px", marginBottom: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: "#1e3a8a" }}>
                            {uploadProgress.error
                                ? `⚠ Stopped at chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}`
                                : uploading
                                    ? `Uploading chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}…`
                                    : `Upload complete`}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
                            {uploadProgress.uploaded.toLocaleString()} / {uploadProgress.total.toLocaleString()} rows
                            &nbsp;({Math.round((uploadProgress.uploaded / uploadProgress.total) * 100)}%)
                        </span>
                    </div>
                    <div style={{ height: 10, background: "#e5e7eb", borderRadius: 999, overflow: "hidden" }}>
                        <div style={{
                            height: "100%",
                            width: `${(uploadProgress.uploaded / uploadProgress.total) * 100}%`,
                            background: uploadProgress.error ? "#ef4444" : "linear-gradient(90deg,#1e40af,#3b82f6)",
                            borderRadius: 999,
                            transition: "width 0.3s ease",
                        }} />
                    </div>
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
                        {uploadProgress.total.toLocaleString()} total rows · {uploadProgress.totalChunks} chunk{uploadProgress.totalChunks > 1 ? "s" : ""} of {CHUNK_SIZE} rows each
                    </div>
                </div>
            )}

            {/* Upload */}
            <div className="card grid-upload">
                <input
                    type="file"
                    accept=".xlsx,.xls"
                    style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 12px", background: "#fff" }}
                    onChange={(e) => setFile(e.target.files[0])}
                />
                <button className="btn btn-primary" onClick={uploadExcel} disabled={uploading}>
                    {uploading ? `Uploading chunk ${uploadProgress?.chunk ?? ""}/${uploadProgress?.totalChunks ?? ""}…` : "Upload Excel"}
                </button>
                <button className="btn btn-outline" onClick={downloadTemplate}>
                    Download Excel Template
                </button>
            </div>

            {/* Table */}
            <div className="card">
                <div className="card-top">
                    <div className="card-title" style={{ fontWeight: "800" }}>Pre / Post Score Table</div>
                    <div className="table-actions" style={{ marginTop: "1rem" }}>
                        <input
                            className="search"
                            placeholder="Search by beneficiary ID, name, batch ID, center ID..."
                            style={{ marginRight: "1rem", minWidth: 300 }}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <button className="btn" onClick={exportExcel} disabled={exporting}>
                            {exporting ? "Processing..." : "Export Excel"}
                        </button>
                    </div>
                </div>

                <div className="table-wrap">
                    <table className="ep-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                {/* <th>ID</th> */}
                                <th>Beneficiary ID</th>
                                <th>Beneficiary Name</th>
                                <th>Pre Test Score</th>
                                <th>Post Test Score</th>
                                {/* <th>Batch ID</th>
                                <th>Center ID</th>
                                <th>Project ID</th> */}
                                <th>Created At</th>
                                {/* <th>Actions</th> */}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.slice(0, visibleCount).map((s, index) => (
                                <tr key={s.id}>
                                    <td>{index + 1}</td>
                                    {/* <td>{s.id}</td> */}
                                    <td>{s.beneficiaryId}</td>
                                    <td>{s.beneficiaryName}</td>
                                    <td>{s.preTestScore}</td>
                                    <td>{s.postTestScore}</td>
                                    {/* <td>{s.batchId}</td>
                                    <td>{s.centerId}</td>
                                    <td>{s.projectId}</td> */}
                                    <td>{s.createdAt}</td>
                                    {/* <td className="actions">
                                        <button
                                            className="btn btn-sm btn-danger"
                                            onClick={() => deleteScore(s.id)}
                                        >
                                            Delete
                                        </button>
                                    </td> */}
                                </tr>
                            ))}

                            {!filtered.length && (
                                <tr>
                                    <td colSpan={COL_COUNT} className="muted">
                                        {loading ? "Loading..." : "No records found"}
                                    </td>
                                </tr>
                            )}
                            {!allLoaded && !loading && (
                                <tr>
                                    <td colSpan={COL_COUNT} style={{ textAlign: "center", padding: "14px" }}>
                                        <button className="btn btn-outline" onClick={fetchAllScores} disabled={loadingAll}>
                                            {loadingAll ? "Loading all data…" : "Load All Data"}
                                        </button>
                                    </td>
                                </tr>
                            )}
                            {allLoaded && filtered.length > visibleCount && (
                                <tr>
                                    <td colSpan={COL_COUNT} style={{ textAlign: "center", padding: "14px" }}>
                                        <button
                                            className="btn btn-outline"
                                            onClick={() => setVisibleCount((c) => c + 100)}
                                        >
                                            Show More ({filtered.length - visibleCount} remaining)
                                        </button>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <style>{`
                .cmp-page { padding:22px; background:#f7f7fb; min-height:100vh; font-family:Inter }
                h1 { font-size:26px; font-weight:900 }

                .flash { padding:10px; border-radius:10px; margin:10px 0; font-weight:700 }
                .flash-ok { background:#ecfdf5; color:#065f46 }
                .flash-err { background:#fef2f2; color:#991b1b }

                .card { background:#fff; border:1px solid #e5e7eb; border-radius:14px; padding:16px; margin-bottom:14px }
                .grid-upload { display:grid; grid-template-columns:1fr auto auto; gap:10px; align-items:center }

                .btn { border:1px solid #e5e7eb; padding:10px 14px; border-radius:12px; font-weight:800; cursor:pointer; background:#fff }
                .btn-primary { background:#2563eb; color:#fff; border-color:#2563eb }
                .btn-outline { background:#fff; color:#2563eb; border-color:#2563eb }
                .btn-sm { padding:6px 10px; font-size:12px }
                .btn-danger { color:#991b1b; border-color:#fca5a5 }

                .search { padding:10px; border-radius:12px; border:1px solid #e5e7eb; margin-bottom:10px }

                .table-wrap { overflow-x:auto }
                .ep-table { width:100%; min-width:800px; border-collapse:collapse }
                th, td { padding:10px; border-bottom:1px solid #e5e7eb; font-size:13px; white-space:nowrap }
                th { color:#6b7280; text-align:left }

                .actions { display:flex; gap:8px }
                .muted { color:#6b7280; font-weight:700 }
            `}</style>
        </div>
    );
}
